import Editor from '@monaco-editor/react';
import Box from '@mui/material/Box';

// ----------------------------------------------------------------------

/**
 * Shared Monaco-based JSON editor used everywhere JSON is edited/displayed.
 *
 * - `value` may be a string (kept verbatim) or an object (pretty-printed).
 * - `onChange(string)` receives the raw editor text.
 * - `onMount(editor, monaco)` hands over the editor instance.
 * - `height` accepts a number (px) or any CSS length (e.g. `calc(100vh - 480px)`).
 */
export function MonacoJsonEditor({
  value,
  onChange,
  onMount,
  readOnly = false,
  height = 240,
  wrap = false,
  options,
  sx,
}) {
  const text =
    typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value, null, 2);

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: (theme) => `solid 1px ${theme.palette.divider}`,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Editor
        height={height}
        language="json"
        value={text}
        onChange={(next) => onChange?.(next ?? '')}
        onMount={onMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 13,
          tabSize: 2,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          wordWrap: wrap ? 'on' : 'off',
          lineNumbers: 'on',
          formatOnPaste: true,
          // Let the page scroll when the editor doesn't need to.
          scrollbar: { alwaysConsumeMouseWheel: false },
          ...options,
        }}
      />
    </Box>
  );
}
